import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { RootState, useDispatch, useSelector } from "src/store";
import { showErrorToast, showSuccessToast } from "src/ui/shared/toasts/Toasts";
import { makeStyles } from "tss-react/mui";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import { RoleLabel } from "@dashboard/shared/components/RoleLabel";
import { getRoleLabel } from "@dashboard/shared/roles";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { closeDetails, updateGroupMembers } from "@devices/store";
import { selectGroupById } from "@groups/store/selectors";
import { Box, DialogContentText, MenuItem, Select, SelectChangeEvent, Theme, Typography } from "@mui/material";
import AuthService from "@services/authService";
import { memberSubject, PermissionService } from "@services/permissionService/permissionService";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { IndicatorIcon } from "@shared/IndicatorIcon";
import { PendingStatusIcon } from "@shared/StatusLabel";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { Tooltip } from "@shared/Tooltip";
import { CellContext } from "@tanstack/react-table";

import { TableData } from "./MemberTab";

const useStyles = makeStyles()((theme: Theme) => ({
    borderless: {
        borderBottom: "none !important",
    },
    lineThrough: {
        textDecorationLine: "line-through",
        textDecorationStyle: "solid",
    },
    inputItem: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        flexShrink: 0,
        "& .MuiInputBase-root": {
            height: 36,
        },
    },
    nameContainer: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    buttonBox: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    membershipIcon: {
        marginBottom: -2,
    },
    iconBox: {
        display: "flex",
        justifyContent: "center",
        width: 25,
    },
}));

export const EmailCell = (props: CellContext<TableData, unknown>) => {
    const { t } = useTranslation(["sidepanels", "common"]);
    const { classes } = useStyles();
    const { email, groupId, status, isWorkspaceOnly } = props.row.original;

    const pendingTooltipMessage = isWorkspaceOnly ? (
        <span>{t("sidepanels:groupPanel.tooltip.pendingWorkspace")}</span>
    ) : (
        <span>{t("sidepanels:groupPanel.tooltip.pendingGroup")}</span>
    );

    if (isWorkspaceOnly) {
        return (
            <MinimalTableCell className={classes.borderless} align="justify">
                <div className={classes.nameContainer}>
                    <Box className={classes.iconBox}>
                        <IndicatorIcon
                            icon="fa-solid fa-house-user"
                            tooltip={t("sidepanels:groupPanel.tooltip.workspaceMember")}
                            className={classes.membershipIcon}
                        />
                    </Box>
                    <Typography>{email}</Typography>
                    {status === "Pending" && <PendingStatusIcon tooltip={pendingTooltipMessage} />}
                </div>
            </MinimalTableCell>
        );
    }

    return (
        <MinimalTableCell className={classes.borderless} align="justify">
            <Box className={classes.nameContainer}>
                {groupId ? (
                    <Box className={classes.iconBox}>
                        <IndicatorIcon
                            icon="fa-solid fa-user"
                            tooltip={t("sidepanels:groupPanel.tooltip.groupMember")}
                            className={classes.membershipIcon}
                        />
                    </Box>
                ) : null}
                <Typography>{email ?? "-"} </Typography>
                {status === "Pending" && <PendingStatusIcon tooltip={pendingTooltipMessage} />}
            </Box>
        </MinimalTableCell>
    );
};
export const RoleCell = (props: CellContext<TableData, unknown>) => {
    const [pendingRole, setPendingRole] = useState<TableData["role"] | null>(null);

    const { t } = useTranslation(["sidepanels", "modals", "common"]);
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const { email, role, groupId, isWorkspaceOnly } = props.row.original;

    const isExpired = useCurrentSubscriptionExpired();
    const group = useSelector((state: RootState) => (groupId ? selectGroupById(state, groupId) : null));
    const { data: workspace } = useCurrentWorkspace();
    const currentUser = AuthService.getCurrentUser();
    const workspaceRole = workspace?.members.find((workspaceMember) => workspaceMember.email === email)?.role;

    const { groupAbility } = PermissionService();
    const ability = groupAbility(group || null, workspace);
    const mayManageMembers = ability.can("manage", "Member");
    const canManageOwners = ability.can("update", memberSubject("Owner"));
    // A Manager may neither change an owner's role nor assign the owner role (mirrors the backend permission checks).
    const cannotEditOwner = !canManageOwners && role === "Owner";
    const mayEditRole = ability.can("update", memberSubject(role));
    const userIsWorkspaceMember = workspace?.members.some((member) => member.userId === currentUser?.sub);

    const onRoleChange = (event: SelectChangeEvent<typeof role>) => {
        setPendingRole(event.target.value);
    };

    const onConfirmRoleChange = async () => {
        if (!pendingRole) {
            return;
        }

        const data = await dispatch(updateGroupMembers({ id: groupId, changedMembers: { [email]: pendingRole } })).unwrap();
        showSuccessToast(t("sidepanels:groupPanel.toast.memberUpdated"));
        const userIsGroupMember = data[0]?.members.some((item) => item.userId === currentUser?.sub);
        if (!userIsGroupMember && !userIsWorkspaceMember) {
            dispatch(closeDetails());
        }
    };

    if (isWorkspaceOnly) {
        return (
            <MinimalTableCell className={classes.borderless} align="justify">
                <Box className={classes.inputItem}>
                    {mayManageMembers ? (
                        <Tooltip
                            title={t("sidepanels:groupPanel.tooltip.workspaceRole")}
                            placement="top"
                            slotProps={{ popper: { sx: { "& .MuiTooltip-tooltip": { maxWidth: 600 } } } }}
                        >
                            <Select defaultValue="User" disabled style={{ width: 200 }}>
                                <MenuItem value="User">{getRoleLabel(workspaceRole ?? "User")}</MenuItem>
                            </Select>
                        </Tooltip>
                    ) : (
                        <RoleLabel role={workspaceRole ?? "User"} />
                    )}
                </Box>
            </MinimalTableCell>
        );
    }

    return (
        <MinimalTableCell className={classes.borderless} align="justify">
            <Box className={classes.inputItem}>
                {mayManageMembers ? (
                    <Tooltip
                        title={
                            // Expiry blocks every role change, so it outranks the owner-specific message.
                            isExpired
                                ? t("sidepanels:groupPanel.tooltip.subscriptionExpired")
                                : t("sidepanels:groupPanel.tooltip.cannotEditOwnerRole")
                        }
                        hide={!isExpired && !cannotEditOwner}
                        placement="left"
                    >
                        <Select
                            name="member-role-select"
                            onChange={onRoleChange}
                            value={props.row.original.role}
                            disabled={!mayEditRole}
                            style={{ width: 200 }}
                        >
                            <MenuItem value="Owner" disabled={!canManageOwners}>
                                {t("common:role.owner")}
                            </MenuItem>
                            <MenuItem value="Manager">{t("common:role.manager")}</MenuItem>
                            <MenuItem value="User">{t("common:role.user")}</MenuItem>
                        </Select>
                    </Tooltip>
                ) : (
                    <RoleLabel role={role} />
                )}
            </Box>

            <ConfirmDialog
                title={t("modals:changeMemberRole.title")}
                secondaryActionText={t("common:button.cancel")}
                open={!!pendingRole}
                onConfirm={onConfirmRoleChange}
                onClose={() => setPendingRole(null)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="changeMemberRole.descriptionGroup"
                        values={{
                            email,
                            name: group?.name,
                            oldRole: getRoleLabel(role),
                            newRole: getRoleLabel(pendingRole),
                        }}
                        components={{ bold: <strong /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </MinimalTableCell>
    );
};
export const ActionCell = (props: CellContext<TableData, unknown>) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const { t } = useTranslation(["sidepanels", "modals", "common"]);
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const { email, role, groupId, isWorkspaceOnly } = props.row.original;

    const currentUser = AuthService.getCurrentUser();
    const ownEmail = AuthService.getCurrentUser()?.email;
    const { data: workspace } = useCurrentWorkspace();
    const group = useSelector((state: RootState) => (groupId ? selectGroupById(state, groupId) : null));
    const { groupAbility } = PermissionService();
    const ability = groupAbility(group || null, workspace);
    const mayManageMember = ability.can("manage", "Member");
    const mayManageThisMember = ability.can("manage", memberSubject(role));
    const isMemberOwner = role === "Owner";
    const userIsWorkspaceMember = workspace?.members.some((member) => member.userId === currentUser?.sub);

    const isDeleteButtonDisabled = () => {
        if (isMemberOwner && !mayManageThisMember) {
            return true;
        }
        if (email === ownEmail && role === "User") {
            return false;
        }
        return !mayManageMember;
    };
    const getDeleteTooltip = () => {
        if (role === "User" && email === ownEmail) {
            return t("sidepanels:groupPanel.tooltip.deleteMember");
        }
        // Check the general "may manage members" gate before the owner-specific case, so a role that
        // cannot manage members at all (e.g. a plain User) sees the general message instead of the
        // owner-only "Managers cannot delete owners" — which then only reaches actual Managers.
        if (!mayManageMember) {
            return t("sidepanels:groupPanel.tooltip.deleteMemberError");
        }
        if (isMemberOwner && !mayManageThisMember) {
            return t("sidepanels:groupPanel.tooltip.ownerCannotBeDeleted");
        }
        return t("sidepanels:groupPanel.tooltip.deleteMember");
    };
    const onConfirmDelete = () => {
        setLoading(true);
        dispatch(updateGroupMembers({ id: groupId, removedMembers: [email] }))
            .unwrap()
            .then((data) => {
                showSuccessToast(t("sidepanels:groupPanel.toast.memberRemovedSuccess"));
                const userIsGroupMember = data[0]?.members.some((item) => item.userId === currentUser?.sub);
                if (!userIsGroupMember && !userIsWorkspaceMember) {
                    dispatch(closeDetails());
                }
            })
            .catch(({ message }) => showErrorToast(message))
            .finally(() => setLoading(false));
    };

    if (isWorkspaceOnly) {
        return (
            <MinimalTableCell className={classes.borderless} align="right">
                <DangerOutlinedButton
                    disabled
                    loading={loading}
                    tooltipProps={{
                        title: t("sidepanels:groupPanel.tooltip.workspaceMemberCannotBeDeleted"),
                    }}
                >
                    <i className="fa-solid fa-trash" />
                </DangerOutlinedButton>
            </MinimalTableCell>
        );
    }

    return (
        <MinimalTableCell className={classes.borderless} align="right">
            <DangerOutlinedButton
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={isDeleteButtonDisabled()}
                tooltipProps={{ title: getDeleteTooltip() }}
                loading={loading}
            >
                <i className="fa-solid fa-trash" />
            </DangerOutlinedButton>
            <ConfirmDialog
                title={t("modals:removeMember.title")}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={isDeleteModalOpen}
                onConfirm={onConfirmDelete}
                onClose={() => setIsDeleteModalOpen(false)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="removeMember.descriptionGroup"
                        values={{
                            email,
                            role,
                            name: group?.name,
                        }}
                        components={{
                            bold: <strong />,
                        }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </MinimalTableCell>
    );
};
